import { Link } from "react-router-dom";
import type { Article } from "@/types/content";
import { categoryMap } from "@/mocks/categories";
import { formatDate, formatViews } from "@/lib/content";

interface ArticleCardProps {
  article: Article;
  variant?: "vertical" | "horizontal" | "minimal";
  showSummary?: boolean;
}

export default function ArticleCard({ article, variant = "vertical", showSummary = true }: ArticleCardProps) {
  const category = categoryMap[article.category];

  if (variant === "minimal") {
    return (
      <Link to={`/detail/article/${article.id}`} className="group flex items-start gap-3 py-3">
        <span className="mt-0.5 font-editorial text-sm font-semibold text-primary-300 transition-colors group-hover:text-primary-500">
          {category?.name}
        </span>
        <span className="min-w-0 flex-1">
          <span className="line-clamp-2-custom block text-sm font-medium leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-600">
            {article.title}
          </span>
          <span className="mt-1.5 flex items-center gap-2 text-xs text-foreground-400">
            <span>{article.author.name}</span>
            <span className="text-background-400">·</span>
            <span>{formatDate(article.date)}</span>
          </span>
        </span>
      </Link>
    );
  }

  if (variant === "horizontal") {
    return (
      <Link
        to={`/detail/article/${article.id}`}
        className="group flex gap-3 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-200 md:gap-4 md:p-3.5"
      >
        <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-[104px] md:w-36">
          <img
            src={article.cover}
            alt={`${article.title} - 抖阳原创内容`}
            title={`${article.title} - 抖阳原创`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1.5 flex flex-wrap items-center gap-2 text-xs text-foreground-400">
            <span className="text-primary-600">{category?.name}</span>
            <span>·</span>
            <span>{formatDate(article.date)}</span>
          </div>
          <h3 className="line-clamp-2-custom text-sm font-semibold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-700 md:text-base">
            {article.title}
          </h3>
          {showSummary ? (
            <p className="mt-1.5 hidden text-xs leading-relaxed text-foreground-500 md:block line-clamp-2-custom">
              {article.summary}
            </p>
          ) : null}
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/detail/article/${article.id}`}
      className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200"
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={`${article.title} - 抖阳原创内容`}
          title={`${article.title} - 抖阳原创`}
          className="h-full w-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.04]"
        />
        <span className="absolute left-3 top-3 rounded bg-background-50/95 px-2 py-1 text-[11px] font-medium text-foreground-800">
          {category?.name}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="text-base font-semibold leading-relaxed text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
          {article.title}
        </h3>
        {showSummary ? (
          <p className="mt-2 line-clamp-2-custom text-sm leading-relaxed text-foreground-500">{article.summary}</p>
        ) : null}
        <div className="mt-auto flex items-center justify-between gap-2 pt-4 text-xs text-foreground-400">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-3-line" />
            {article.author.name}
          </span>
          <span className="flex items-center gap-2">
            <span>{formatDate(article.date)}</span>
            <span className="text-background-400">|</span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line" />
              {formatViews(article.views)}
            </span>
          </span>
        </div>
      </div>
    </Link>
  );
}